import React from 'react';
import { useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DashboardLoading, NoDataState } from '../../../components/LoadingStates';
import ScoreChart from '../../../components/ScoreChart';
import selectUser from '../../../store/selectors/appSelector';
import useFairReliabilityScore from '../../../utils/useFairReliabilityScore';
import DashboardJobs from '../DashboardJobs';
import DashboardOnboarding from '../DashboardOnboarding';
import { useDashboardData, useDashboardPermissions, useDashboardRendering } from '../hooks';
import { DashboardWidgets } from '../shared';

import useRecruiterDataFetching from './hooks/useRecruiterDataFetching';

/**
 * RecruiterAgencyDashboard component for recruiter's own agency data
 * - Handles recruiters going to /dashboard
 * - Displays agency-specific widgets and data for the recruiter's own agency
 */
const RecruiterAgencyDashboard = () => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const { pickReliability } = useFairReliabilityScore();
  const { recruitersAgencyId } = useDashboardPermissions(user);

  // Recruiter-specific data fetching for their own agency
  const { agencyDetail, isGetAgencyLoading } = useRecruiterDataFetching(recruitersAgencyId);

  const { revenueLabels, workers, revenue, futurePlacementsData } = useDashboardData(
    user,
    null, // No widget data for agency-specific dashboards
    agencyDetail,
    true // Always agency dashboard
  );

  const { isLoading, shouldShowWidgets, shouldShowNoData } = useDashboardRendering(
    false, // No widget data loading for agency-specific dashboards
    isGetAgencyLoading,
    true, // Always agency dashboard
    agencyDetail,
    null // No widget data
  );

  // Recruiters don't have edit permissions, so no edit handler needed

  if (isLoading) {
    return <DashboardLoading />;
  }

  return (
    <Box
      data-testid="recruiter-dashboard"
      padding={theme.components.dashboard.layout.padding}
      display="flex"
      flexDirection="column"
      gap={theme.components.dashboard.layout.sectionGap}
    >
      {/* Recruiter dashboard - no back button or edit options needed */}

      {/* Agency Header */}
      <Stack>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="center">
          <Box>
            <Typography sx={theme.components.dashboard.typography.header}>
              {agencyDetail?.name}
            </Typography>
            <Typography sx={theme.components.dashboard.typography.subHeader}>
              {LanguageConverterV2('dashboard.agency.code')} {agencyDetail?.onboardingId}
            </Typography>
          </Box>
          {workers && (
            <ScoreChart
              label={LanguageConverterV2('dashboard.widget.reliability.title')}
              score={pickReliability(
                workers.averageRawReliability,
                workers.averageReliabilityScore
              )}
              description={LanguageConverterV2('dashboard.widget.reliability.description')}
            />
          )}
        </Stack>
        <Stack direction="row">
          <DashboardOnboarding />
        </Stack>
      </Stack>

      {/* Agency Dashboard Widgets */}
      <DashboardWidgets
        isLoading={isLoading}
        workers={workers}
        revenue={revenue}
        revenueLabels={revenueLabels}
        futurePlacementsData={futurePlacementsData}
        showWidgets={shouldShowWidgets}
      />

      {/* No Data State */}
      {shouldShowNoData && <NoDataState />}

      {/* Agency-specific content */}
      <DashboardJobs agencyId={recruitersAgencyId} />
    </Box>
  );
};

export default RecruiterAgencyDashboard;
